import { ID, LinkedItemSection, Task, Timestamp } from '@griply/types';

export type PairDuplicatedSectionTasksOptions = {
  section: LinkedItemSection;
  // The section's resolved parent tasks (the originals listed in section.tasks).
  sectionTasks: Task[];
  // The exact array handed to duplicateTasks (parents + subtasks).
  oldTasks: Task[];
  // duplicateTasks result — index-aligned with oldTasks.
  newTasks: Task[];
  newSectionId: ID;
};

export type PairDuplicatedSectionTasksResult = {
  newParentTasks: Task[];
  mainTaskPairs: Array<{ oldTask: Task; newTask: Task }>;
  newSection: LinkedItemSection;
};

export const getSectionSubTaskIds = (sectionTasks: Task[]): ID[] =>
  Array.from(new Set(sectionTasks.flatMap(({ childIds }) => childIds ?? [])));

// duplicateTasks returns the new docs in input order — pair them back to their
// originals so linked references (tags, life-area order) can follow the copies.
export const pairDuplicatedSectionTasks = ({
  section,
  sectionTasks,
  oldTasks,
  newTasks,
  newSectionId,
}: PairDuplicatedSectionTasksOptions): PairDuplicatedSectionTasksResult => {
  const newParentTasks = newTasks.filter(({ parentIds }) => !parentIds?.length);

  const oldToNewTaskMap = new Map<ID, Task>();
  oldTasks.forEach((oldTask, index) => {
    const newTask = newTasks[index];
    if (newTask) {
      oldToNewTaskMap.set(oldTask.id, newTask);
    }
  });

  const mainTaskPairs = sectionTasks
    .filter((task) => !task.parentIds?.length)
    .map((oldTask) => {
      const newTask = oldToNewTaskMap.get(oldTask.id);
      return newTask ? { oldTask, newTask } : null;
    })
    .filter((pair): pair is { oldTask: Task; newTask: Task } => pair !== null);

  const newSection: LinkedItemSection = {
    id: newSectionId,
    name: section.name,
    createdAt: new Date() as unknown as Timestamp,
    tasks: newParentTasks.map((task) => task.id),
  };

  return { newParentTasks, mainTaskPairs, newSection };
};
